import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axios";
import DOMPurify from "dompurify";
import { truncateHtml } from "../utils/authHelpers";
import gsap from "gsap";

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref for related blog cards
  const relatedCardsRef = useRef([]);

  // Add this state for tab selection
  const [mediaTab, setMediaTab] = useState("images");

  useEffect(() => {
    axiosInstance
      .get(`/api/blogs/slug/${slug}`)
      .then((response) => {
        setBlog(response.data);
        setLoading(false);

        const categoryId = response.data.category?.id || null;
        const labelIds = response.data.labels?.map((label) => label.label.id) || [];

        fetchRelatedBlogs(categoryId, labelIds);
      })
      .catch(() => {
        setError("Failed to fetch blog details.");
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    if (window.innerWidth >= 1024 && relatedCardsRef.current.length > 0) {
      gsap.from(relatedCardsRef.current, {
        opacity: 0,
        y: 20,
        stagger: 0.12,
        duration: 0.7,
        ease: "power3.out",
        clearProps: "all"
      });
    }
  }, [relatedBlogs]);

  const fetchRelatedBlogs = (categoryId, labelIds) => {
    if (!categoryId && labelIds.length === 0) return;

    axiosInstance
      .get(`/api/blogs/related/categories`, {
        params: {
          category_id: categoryId,
          label_ids: labelIds.length > 0 ? labelIds.join(",") : null,
        },
      })
      .then((response) => {
        setRelatedBlogs(response.data.filter((blog) => blog.slug !== slug));
      })
      .catch(() => {
        setRelatedBlogs([]);
      });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-5 flex flex-col lg:flex-row gap-6">
        {/* Main Skeleton */}
        <div className="lg:w-2/3 space-y-4">
          <Skeleton className="h-10 w-2/3 mx-auto" />
          <Skeleton className="h-4 w-1/3 mx-auto" />
          <Skeleton className="h-80 w-full my-4" />
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-40 w-full mt-4" />
        </div>
        {/* Related Skeleton */}
        <aside className="lg:w-1/3 bg-gray-50 p-4 rounded-md shadow-md space-y-4">
          <Skeleton className="h-6 w-1/2 mb-3" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/4" />
            </div>
          ))}
        </aside>
      </div>
    );
  }

  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!blog) return <p className="text-center text-red-500">No blog found.</p>;

  return (
    <div className="flex flex-col lg:flex-row gap-8 items-center justify-center mx-auto w-full px-2 sm:px-4">
      {/* Main Blog Content */}
      <div className="lg:w-2/3 bg-white flex flex-col">
        <h1 className="text-2xl font-bold text-center mb-4 text-gray-800 uppercase">
          {blog.title}
        </h1>
        <p className="mb-4 text-gray-700 md:text-sm leading-relaxed">
          Published on: {new Date(blog.createdAt).toLocaleDateString()}
        </p>

        {/* Media Tabs */}
        {(blog.images?.length > 0 || blog.videos?.length > 0) ? (
          <div className="mb-2">
            <div className="flex gap-2 mb-4">
              {blog.images?.length > 0 ? (
                <button
                  className={`px-4 py-2 rounded-t-lg font-semibold ${
                    mediaTab === "images"
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                  onClick={() => setMediaTab("images")}
                >
                  Images
                </button>
              ) : blog.videos?.length > 0 ? (
                <button
                  className={`px-4 py-2 rounded-t-lg font-semibold ${
                    mediaTab === "videos"
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                  onClick={() => setMediaTab("videos")}
                >
                  Videos
                </button>
              ) : null}
             
            </div>
            <div className="w-full">
              {mediaTab === "images" &&
                blog.images?.map((img, idx) => (
                  <img
                    key={img.image_url || img.url || idx}
                    src={img.image_url || img.url}
                    alt={blog.title}
                    className="w-full h-96 object-cover rounded-xl shadow"
                    loading="lazy"
                  />
                ))}
              {mediaTab === "videos" &&
                blog.videos?.map((vid, idx) => (
                  <video
                    key={vid.video_url || vid.url || idx}
                    src={vid.video_url || vid.url}
                    controls
                    className="w-full h-96 object-cover rounded-xl shadow bg-gray-100"
                    style={{ background: "#eee" }}
                  />
                ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          {blog.category && (
            <span className="bg-red-50 text-red-600 md:text-sm font-semibold px-4 py-1 rounded-full border border-red-100 shadow-sm">
              {blog.category.name}
            </span>
          )}  
          {blog.labels?.length > 0 && (
            <span className="bg-red-50 text-red-600 md:text-sm font-semibold px-4 py-1 rounded-full border border-red-100 shadow-sm">
              {blog.labels.map((label) => label.label.name).join(", ")}
            </span>
          )}
        </div>

        <div
          className="mt-2 text-gray-900 leading-relaxed prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content) }}
        />
      </div>
      {/* Related Blogs Sidebar */}
      <aside
        className="lg:w-1/3 w-full bg-white p-6 rounded-2xl shadow-xl flex flex-col items-start sticky top-8 self-start h-fit min-h-[200px] max-h-[90vh] overflow-y-auto border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 w-full">Related Blogs</h2>
        {relatedBlogs.length > 0 ? (
          <div className="flex flex-col gap-4 w-full">
            {relatedBlogs.map((relatedBlog, idx) => (
              <div
                key={relatedBlog.id}
                ref={el => relatedCardsRef.current[idx] = el}
                className="mb-4 cursor-pointer hover:shadow-lg rounded-lg transition-all border border-gray-100 hover:border-red-200 group overflow-hidden bg-white"
                onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
              >
                <div className="flex">
                  {/* Image Section */}
                  <div className="w-1/3 relative">
                    {relatedBlog.videos?.length > 0 ? (
                      <video
                        src={relatedBlog.videos[0].video_url || relatedBlog.videos[0].url}
                        className="w-full h-full object-cover min-h-[140px]"
                        muted
                        autoPlay
                        loop
                        playsInline
                        poster={relatedBlog.images?.[0]?.image_url}
                      />
                    ) : relatedBlog.images?.length > 0 ? (
                      <img
                        src={relatedBlog.images[0].image_url || relatedBlog.images[0].url}
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover min-h-[140px]"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center min-h-[140px]">
                        <img
                        alt={blog.title}
                        src="https://placehold.co/400"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        />
                    </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="w-2/3 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-full">
                        {relatedBlog.category?.name || "Uncategorized"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(relatedBlog.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-800 group-hover:text-red-600 mb-2 line-clamp-2">
                      {relatedBlog.title}
                    </h3>

                    <div 
                      className="text-sm text-gray-600 line-clamp-2 mb-2"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(truncateHtml(relatedBlog.content, 100))
                      }}
                    />

                    {relatedBlog.labels?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {relatedBlog.labels.map((label, i) => (
                          <span 
                            key={i}
                            className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full"
                          >
                            {label.label.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Read More Indicator */}
                  <div className="px-4 py-2 border-t border-gray-50 bg-gray-50 group-hover:bg-red-50 transition-colors">
                  <span className="text-sm text-red-600 group-hover:text-red-700 flex items-center">
                    Read More
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No related blogs available.</p>
        )}
      </aside>
    </div>
  );
};

export default BlogDetail;
